import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { map, takeUntil, tap, shareReplay, Observable } from 'rxjs';
import { FormControl } from '@angular/forms';
import { BaseComponent } from '../base-component/base.component';
import { Tag as PapTag, Tag } from '@isophi/pap-api';
import { Tag as ManagerTag } from '@isophi/mng-api';
import { mixWithWhite } from '../../utils/color.utils';
import { CompetenceGroup, CompetenceRow, groupCompetences, isMsmtFramework } from './competence-groups';

export type TagCategory = Tag & { categories: Array<Tag> };
@Component({
    selector: 'isophi-app-portfolio-plan-competence',
    templateUrl: './portfolio-plan-competence.component.html',
    styleUrls: ['./portfolio-plan-competence.component.scss'],
    standalone: false
})
export class PortfolioPlanCompetenceComponent extends BaseComponent implements OnInit {
  @Input()
  control!: FormControl<string[]>;
  @Input()
  fetchCategories!: () => Observable<PapTag[] | ManagerTag[]>;
  @Input()
  allowEditMode = true;
  @Input()
  materialServer!: string;

  @Input() withImages = true;

  @Output()
  requestSave$ = new EventEmitter<string[]>();

  initValue: string[] = [];
  collapsedStates: Record<string, boolean> = {};

  tags$!: Observable<Tag[]>;
  // MŠMT framework: compact list grouped by areas
  groups$!: Observable<CompetenceGroup[]>;
  // Old framework: original cards with icons
  categories$!: Observable<TagCategory[]>;
  selectedCategories$!: Observable<Tag[]>;

  ngOnInit(): void {
    this.initValue = this.control?.value ?? [];
    this.tags$ = this.fetchCategories().pipe(
      takeUntil(this.ngUnsubscribe$),
      map((tags) => tags as Tag[]),
      tap((tags) => {
        this.collapsedStates = {};
        tags.filter((tag) => tag.parentId).forEach((tag) => (this.collapsedStates[tag.uuid] = true));
      }),
      shareReplay(1)
    );
    this.groups$ = this.tags$.pipe(map((tags) => groupCompetences(tags)));
    this.categories$ = this.tags$.pipe(
      map((categories) => {
        const parentCategories: Record<number, TagCategory> = {};

        categories.forEach((category) => {
          const createParentCategory = (parentId: number) => {
            parentCategories[parentId] = {
              ...category,
              categories: [],
            };
          };

          if (!category.parentId) {
            createParentCategory(category.id);
            return;
          }

          if (!parentCategories[category.parentId]) {
            createParentCategory(category.parentId);
          }

          parentCategories[category.parentId].categories.push(category);
        });

        const mappedCategories = Object.entries(parentCategories).map(([_, category]) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          _;
          return category;
        });
        return mappedCategories;
      }),
      shareReplay(1)
    );
    this.selectedCategories$ = this.categories$.pipe(
      map((categories) => {
        const allCategories = categories.reduce((acc, current) => acc.concat(current.categories), [] as Tag[]);

        return allCategories?.filter((cat) => this.control.value?.includes(cat.uuid)) ?? [];
      })
    );
  }

  /** The record's framework: by its selected competences (the director's view gets tags of both frameworks). */
  isCompact(tags: Tag[]): boolean {
    const selected = tags.filter((tag) => this.isSelected(tag.uuid));
    return isMsmtFramework(selected.length > 0 ? selected : tags);
  }

  isSelected(uuid: string): boolean {
    return this.control.value?.includes(uuid) ?? false;
  }

  hasSelected(group: CompetenceGroup): boolean {
    return group.rows.some((row) => this.isSelected(row.tag.uuid));
  }

  rowBackground(row: CompetenceRow, highlighted = false): string | null {
    return row.color ? mixWithWhite(row.color, highlighted ? 0.5 : 0.25) : null;
  }

  trackRow(_: number, row: CompetenceRow): string {
    return row.tag.uuid;
  }

  getCompetenceImageUrl(url: string): string {
    return `${this.materialServer}${url}`;
  }

  toggleCollapse(uuid: string): void {
    this.collapsedStates[uuid] = !this.collapsedStates[uuid];
  }

  handleCategorySelect(event: Event, categoryUuid: string): void {
    event.stopPropagation();
    const input = event.target as HTMLInputElement;
    const checked = input.checked;

    if (checked) {
      this.control.setValue([...this.control.value, categoryUuid]);
    } else {
      this.control.setValue(this.control.value.filter((uuid) => uuid !== categoryUuid));
    }
  }

  cancel(): void {
    this.control.setValue(this.initValue);
  }

  save(): void {
    this.initValue = this.control.value;
    this.requestSave$.emit(this.control.value);
  }
}
